import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  PenTool, Languages, FileText, Bot, MessageSquare,
  Users, Plus, ArrowRight, HelpCircle, Settings,
  ExternalLink, ChevronDown, X, Loader2, Sparkles,
  CheckCircle, Target, Lightbulb, ListChecks, User, Trash2, Pencil
} from 'lucide-react';
import { generateTranscriptSummary } from '../../../services/openaiService';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';
import { useSiteContext } from '../../../context/SiteContext';
import { useMeetingState } from '../../../context/MeetingStateContext';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt-br', name: 'Portuguese (Brazil)' },
  { code: 'it', name: 'Italian' },
  { code: 'zh-cn', name: 'Chinese (Simplified)' },
  { code: 'zh-tw', name: 'Chinese (Traditional)' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'ru', name: 'Russian' },
  { code: 'tr', name: 'Turkish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'hi', name: 'Hindi' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'id', name: 'Indonesian (Bahasa)' },
  { code: 'th', name: 'Thai' },
  { code: 'uk', name: 'Ukrainian' },
];

// Toggle Switch Component
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        checked ? 'bg-[#6a1fbf]' : 'bg-gray-600'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// Meeting Notes Modal Component
function MeetingNotesModal({ isOpen, onClose, aiSummary, isLoading, transcriptHistory }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1f35] rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col border border-white/10">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold">AI Meeting Notes</h2>
              <p className="text-xs text-gray-400">Powered by OpenAI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#6a1fbf] animate-spin mb-3" />
              <p className="text-gray-400 text-sm">Analyzing meeting content...</p>
              <p className="text-gray-500 text-xs mt-1">This may take a moment</p>
            </div>
          ) : aiSummary ? (
            <>
              {/* Summary Section */}
              {aiSummary.summary && (
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-[#6a1fbf]" />
                    <h3 className="text-white font-medium text-sm">Summary</h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {aiSummary.summary}
                  </p>
                </div>
              )}

              {/* Key Points */}
              {aiSummary.keyPoints && aiSummary.keyPoints.length > 0 && (
                <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-yellow-400" />
                    <h3 className="text-yellow-400 font-medium text-sm">Key Points</h3>
                  </div>
                  <ul className="space-y-2">
                    {aiSummary.keyPoints.map((point, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                        <span className="text-yellow-400 mt-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Items */}
              {aiSummary.actionItems && aiSummary.actionItems.length > 0 && (
                <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <ListChecks className="w-4 h-4 text-blue-400" />
                    <h3 className="text-blue-400 font-medium text-sm">Action Items</h3>
                  </div>
                  <ul className="space-y-2">
                    {aiSummary.actionItems.map((item, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Decisions */}
              {aiSummary.decisions && aiSummary.decisions.length > 0 && (
                <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-purple-400" />
                    <h3 className="text-purple-400 font-medium text-sm">Decisions Made</h3>
                  </div>
                  <ul className="space-y-2">
                    {aiSummary.decisions.map((decision, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                        <span className="text-purple-400 mt-1">→</span>
                        <span>{decision}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Topics */}
              {aiSummary.topics && aiSummary.topics.length > 0 && (
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-green-400" />
                    <h3 className="text-green-400 font-medium text-sm">Topics Discussed</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {aiSummary.topics.map((topic, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Bot className="w-12 h-12 text-gray-600 mb-3" />
              <p className="text-gray-400 text-sm">No meeting notes available yet</p>
              <p className="text-gray-500 text-xs mt-1">
                {transcriptHistory.length === 0
                  ? 'Start speaking to generate transcript'
                  : 'Click "Generate AI Summary" to analyze'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-white/5">
          <p className="text-[10px] text-gray-500 text-center">
            AI-generated summary • May not capture all details
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ToolsPanel({
  onOpenWhiteboard,
  onCloseWhiteboard,
  isWhiteboardOpen = false,
  onOpenBreakoutRooms,
  currentRoomId,
  onDisplayQuestion,
  participants = [],
  // AI Translation props (synced with bottom bar)
  translationEnabled: externalTranslationEnabled,
  setTranslationEnabled: externalSetTranslationEnabled,
  selectedLanguage: externalSelectedLanguage,
  setSelectedLanguage: externalSetSelectedLanguage,
  startTranslation,
  stopTranslation,
  // AI Captions props
  aiCaptionsEnabled,
  handleToggleAICaptions,
  // Transcript history (processed by parent)
  transcriptHistory: externalTranscriptHistory = [],
  // User's rooms from dashboard (fetched by parent)
  userRooms: externalUserRooms = [],
  // AI Notetaker STT controls
  sttBotActive: externalSttBotActive,
  onStartNotetaker,
  onStopNotetaker,
}) {
  // Toggle is independent local state — it represents the user's intent to
  // show the "Open Whiteboard" button. Closing the whiteboard via the X
  // button should NOT flip the toggle off; the toggle stays ON so the user
  // can reopen without re-toggling.
  const [whiteboardToggle, setWhiteboardToggle] = useState(false);
  // Use external translation state if provided, otherwise use internal state
  const [internalTranslationsActive, setInternalTranslationsActive] = useState(false);
  const [internalSelectedLanguage, setInternalSelectedLanguage] = useState('en');

  const translationsActive = aiCaptionsEnabled !== undefined ? aiCaptionsEnabled : (externalTranslationEnabled !== undefined ? externalTranslationEnabled : internalTranslationsActive);
  const selectedLanguage = externalSelectedLanguage || internalSelectedLanguage;

  const handleTranslationToggle = (value) => {
    // Sync with AI Captions (bottom bar CC button)
    if (handleToggleAICaptions) {
      // Only toggle if the current state doesn't match the desired value
      if (value !== aiCaptionsEnabled) {
        handleToggleAICaptions();
      }
    } else if (externalSetTranslationEnabled) {
      externalSetTranslationEnabled(value);
      if (value && startTranslation) {
        startTranslation();
      } else if (!value && stopTranslation) {
        stopTranslation();
      }
    } else {
      setInternalTranslationsActive(value);
    }
  };

  const handleLanguageChange = (lang) => {
    if (externalSetSelectedLanguage) {
      externalSetSelectedLanguage(lang);
    } else {
      setInternalSelectedLanguage(lang);
    }
  };

  // Source of truth = broadcast notetaker state from MeetingStateContext.
  // Falls back to legacy `externalSttBotActive` prop when context is unavailable.
  const { notetaker: notetakerCtxState } = useMeetingState();
  const notetakerActive = notetakerCtxState?.isActive ?? !!externalSttBotActive;
  // Setter is a no-op shim — local state is no longer authoritative, but we keep
  // the call sites compiling. Real toggles go through onStartNotetaker/onStopNotetaker.
  const setNotetakerActive = () => {};
  const [notetakerStarting, setNotetakerStarting] = useState(false);
  const [qaActive, setQaActive] = useState(false);

  // Get meeting role from SiteContext for host check
  const { meetingRole, user } = useSiteContext();
  const isBasicPlan = user?.plan !== "Business";
  const localIsHost = ['moderator', 'co-host', 'host'].includes(meetingRole);

  // Use real breakout room context
  const {
    rooms: breakoutRooms,
    isActive: breakoutRoomsActive,
    isSetup: breakoutSetupMode,
    isHost,
    helpRequests,
    setShowBreakoutModal,
    createSession,
    addRoom,
    removeRoom,
    renameRoom,
    closeRooms,
    session: breakoutSession,
  } = useBreakoutRooms();

  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState('');

  const startEditingRoom = (room) => {
    setEditingRoomId(room.roomId);
    setEditingRoomName(room.roomName || '');
  };
  const cancelEditingRoom = () => {
    setEditingRoomId(null);
    setEditingRoomName('');
  };
  const commitEditingRoom = () => {
    const id = editingRoomId;
    const trimmed = editingRoomName.trim();
    if (!id) return;
    const current = (breakoutRooms || []).find(r => r.roomId === id);
    if (!current || !trimmed || trimmed === current.roomName) {
      cancelEditingRoom();
      return;
    }
    renameRoom(id, trimmed);
    cancelEditingRoom();
  };

  const userRooms = externalUserRooms;
  const [breakoutToggle, setBreakoutToggle] = useState(false);
  const [showBreakoutEndConfirm, setShowBreakoutEndConfirm] = useState(false);
  const addingRoomToolsRef = useRef(false);

  const [privateChatParticipant, setPrivateChatParticipant] = useState('');
  // Inline confirmation shown when the user tries to disable the whiteboard
  // toggle while the board is open. Replaces the native window.confirm so the
  // message renders inside the Tools panel styling (and isn't ever blocked).
  const [showWhiteboardDisableConfirm, setShowWhiteboardDisableConfirm] = useState(false);

  // AI Notetaker state
  const [aiSummary, setAiSummary] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const transcriptContainerRef = useRef(null);

  // Participant transcript view state
  const [transcriptViewMode, setTranscriptViewMode] = useState('all'); // 'all' or 'by-participant'
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const participantTranscriptRef = useRef(null);

  // Use external transcript history from parent (processed with echo detection)
  const transcriptHistory = externalTranscriptHistory;

  // Group transcripts by speaker
  const speakerMap = useMemo(() => {
    const map = {};
    transcriptHistory.forEach(entry => {
      const speaker = entry.speaker || 'Unknown';
      if (!map[speaker]) {
        map[speaker] = [];
      }
      map[speaker].push(entry);
    });
    return map;
  }, [transcriptHistory]);

  const uniqueSpeakers = useMemo(() => Object.keys(speakerMap), [speakerMap]);

  // Auto-scroll participant transcript container
  useEffect(() => {
    if (participantTranscriptRef.current) {
      participantTranscriptRef.current.scrollTop = participantTranscriptRef.current.scrollHeight;
    }
  }, [transcriptHistory, selectedSpeaker]);

  // Auto-scroll transcript container
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcriptHistory]);

  // Generate AI Summary
  const handleGenerateSummary = async () => {
    if (transcriptHistory.length === 0) {
      alert('No transcript available to summarize');
      return;
    }

    setIsGeneratingSummary(true);
    setShowNotesModal(true);

    try {
      // Format transcript for API
      const transcriptItems = transcriptHistory.map(t => ({
        timestamp: t.time,
        text: `${t.speaker}: ${t.text}`
      }));

      const summary = await generateTranscriptSummary(transcriptItems);
      setAiSummary(summary);
    } catch (error) {
      console.error('Failed to generate summary:', error);
      setAiSummary({
        summary: 'Failed to generate summary. Please try again.',
        keyPoints: [],
        actionItems: [],
        decisions: [],
        topics: []
      });
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '00:00';
    if (typeof timestamp === 'string' && timestamp.includes(':')) {
      return timestamp;
    }
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 space-y-6">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Settings className="w-4 h-4 text-[#6a1fbf]" />
          Tools
        </h3>

        {/* Whiteboard */}
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <PenTool className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">Whiteboard</p>
                <p className="text-xs text-gray-500">Collaborative drawing</p>
              </div>
            </div>
            <ToggleSwitch
              checked={whiteboardToggle}
              onChange={(checked) => {
                // If a live board is open, always prompt before disabling so
                // nobody loses a drawing to an accidental toggle. If nothing
                // is open yet, just flip the toggle silently — there's no
                // shared state to warn about.
                if (!checked && isWhiteboardOpen) {
                  setShowWhiteboardDisableConfirm(true);
                  return; // keep toggle ON until user confirms
                }
                setWhiteboardToggle(checked);
              }}
            />
          </div>
          {whiteboardToggle && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <button
                className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                onClick={onOpenWhiteboard}
              >
                Open Whiteboard
              </button>
              <p className="text-[10px] text-gray-500 text-center mt-2">
                Opens collaborative board for all attendees
              </p>
            </div>
          )}

          {/* Inline confirmation when disabling an open whiteboard — rendered
               BELOW the Open Whiteboard button so the button stays visible and
               the host never sees the panel "disappear". */}
          {showWhiteboardDisableConfirm && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg space-y-2">
              <p className="text-xs text-red-300">
                Turning off the whiteboard will close it for everyone in the meeting and any unsaved drawings will be lost.
              </p>
              <div className="flex gap-2">
                <button
                  className="flex-1 py-1.5 px-3 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-colors"
                  onClick={() => {
                    setShowWhiteboardDisableConfirm(false);
                    setWhiteboardToggle(false);
                    if (onCloseWhiteboard) onCloseWhiteboard();
                  }}
                >
                  Close &amp; Disable
                </button>
                <button
                  className="flex-1 py-1.5 px-3 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-medium transition-colors"
                  onClick={() => setShowWhiteboardDisableConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* AI Translations */}
        <div className={`bg-white/5 rounded-xl p-4 ${isBasicPlan ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Languages className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">AI Translations</p>
                <p className="text-xs text-gray-500">Real-time captions</p>
              </div>
            </div>
            <ToggleSwitch
              checked={translationsActive}
              onChange={handleTranslationToggle}
            />
          </div>
          {translationsActive && (
            <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Target Language</label>
                <div className="relative">
                  <select
                    value={selectedLanguage}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code} className="bg-[#001233]">
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-green-400">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                Translating to {languages.find(l => l.code === selectedLanguage)?.name}
              </div>
            </div>
          )}
        </div>

        {/* AI Notetaker */}
        <div className={`bg-gradient-to-br from-[#6a1fbf]/10 to-[#00c853]/10 rounded-xl p-4 border border-[#6a1fbf]/20 ${isBasicPlan ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">AI Notetaker</p>
                <p className="text-xs text-gray-400">Powered by OpenAI</p>
              </div>
            </div>
            <ToggleSwitch
              checked={notetakerActive}
              onChange={async (value) => {
                if (value) {
                  // Start STT bot when notetaker is enabled
                  setNotetakerStarting(true);
                  try {
                    if (onStartNotetaker) {
                      const started = await onStartNotetaker();
                      if (!started) {
                        console.warn('[Notetaker] STT failed to start, enabling UI anyway');
                      }
                    }
                  } catch (err) {
                    console.error('[Notetaker] Error starting STT:', err);
                  } finally {
                    setNotetakerStarting(false);
                  }
                  setNotetakerActive(true);
                } else {
                  // Stop STT bot when notetaker is disabled
                  setNotetakerActive(false);
                  setAiSummary(null);
                  if (onStopNotetaker) {
                    onStopNotetaker();
                  }
                }
              }}
            />
          </div>

          {!notetakerActive ? (
            <div className="space-y-3 mt-3">
              <p className="text-xs text-gray-400">
                Enable to automatically capture and analyze your meeting:
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-black/20 rounded-lg p-2 text-center">
                  <FileText className="w-4 h-4 text-[#6a1fbf] mx-auto mb-1" />
                  <p className="text-[10px] text-gray-400">Smart Summary</p>
                </div>
                <div className="bg-black/20 rounded-lg p-2 text-center">
                  <span className="text-sm">✓</span>
                  <p className="text-[10px] text-gray-400">Action Items</p>
                </div>
                <div className="bg-black/20 rounded-lg p-2 text-center">
                  <span className="text-sm">🎯</span>
                  <p className="text-[10px] text-gray-400">Decisions</p>
                </div>
                <div className="bg-black/20 rounded-lg p-2 text-center">
                  <span className="text-sm">💡</span>
                  <p className="text-[10px] text-gray-400">Insights</p>
                </div>
              </div>
              <p className="text-[10px] text-gray-500 text-center">
                Auto-detects: Sales • Coaching • Internal • Podcast
              </p>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              {/* Recording Status */}
              <div className="bg-black/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  {notetakerStarting ? (
                    <Loader2 className="w-3 h-3 text-[#6a1fbf] animate-spin" />
                  ) : (
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  )}
                  <span className="text-xs text-white font-medium">
                    {notetakerStarting ? 'Starting STT Bot...' : 'Recording'}
                  </span>
                  <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded-full">
                    {transcriptHistory.length} entries
                  </span>
                </div>
                <div
                  ref={transcriptContainerRef}
                  className="space-y-1.5 max-h-24 overflow-y-auto"
                >
                  {transcriptHistory.length === 0 ? (
                    <div className="text-[11px] text-gray-500 text-center py-2">
                      Waiting for speech...
                    </div>
                  ) : (
                    transcriptHistory.slice(-5).map((line, i) => (
                      <div key={line.id || i} className="text-[11px]">
                        <span className="text-gray-600 font-mono">{formatTime(line.time)}</span>
                        <span className="text-gray-400 ml-2">{line.text}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Transcript View Toggle */}
              <div className="bg-black/20 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-white font-medium flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-[#6a1fbf]" />
                    Transcript View
                  </span>
                  <div className="flex bg-black/30 rounded-lg p-0.5">
                    <button
                      onClick={() => { setTranscriptViewMode('all'); setSelectedSpeaker(null); }}
                      className={`px-2 py-1 text-[10px] rounded-md transition-colors ${
                        transcriptViewMode === 'all'
                          ? 'bg-[#6a1fbf] text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setTranscriptViewMode('by-participant')}
                      className={`px-2 py-1 text-[10px] rounded-md transition-colors ${
                        transcriptViewMode === 'by-participant'
                          ? 'bg-[#6a1fbf] text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      By Participant
                    </button>
                  </div>
                </div>

                {transcriptViewMode === 'by-participant' && (
                  <div className="space-y-2">
                    {/* Speaker chips */}
                    {uniqueSpeakers.length === 0 ? (
                      <p className="text-[10px] text-gray-500 text-center py-2">No speakers yet</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {uniqueSpeakers.map(speaker => (
                          <button
                            key={speaker}
                            onClick={() => setSelectedSpeaker(selectedSpeaker === speaker ? null : speaker)}
                            className={`px-2 py-1 text-[10px] rounded-full flex items-center gap-1 transition-colors ${
                              selectedSpeaker === speaker
                                ? 'bg-[#6a1fbf] text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                          >
                            <User className="w-3 h-3" />
                            {speaker}
                            <span className="text-[9px] opacity-70">({speakerMap[speaker]?.length || 0})</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Filtered transcript for selected speaker */}
                    {selectedSpeaker && speakerMap[selectedSpeaker] && (
                      <div
                        ref={participantTranscriptRef}
                        className="space-y-1.5 max-h-40 overflow-y-auto mt-2 pt-2 border-t border-white/10"
                      >
                        {speakerMap[selectedSpeaker].map((line, i) => (
                          <div key={line.id || i} className="text-[11px]">
                            <span className="text-gray-600 font-mono">{formatTime(line.time)}</span>
                            <span className="text-gray-300 ml-2">{line.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* What AI is Capturing */}
              <div className="space-y-2">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">AI is capturing:</p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded-full flex items-center gap-1">
                    <span className="w-1 h-1 bg-green-400 rounded-full animate-pulse" />
                    Transcript
                  </span>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded-full flex items-center gap-1">
                    <span className="w-1 h-1 bg-blue-400 rounded-full animate-pulse" />
                    Action Items
                  </span>
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] rounded-full flex items-center gap-1">
                    <span className="w-1 h-1 bg-purple-400 rounded-full animate-pulse" />
                    Decisions
                  </span>
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded-full flex items-center gap-1">
                    <span className="w-1 h-1 bg-yellow-400 rounded-full animate-pulse" />
                    Key Points
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-2 border-t border-white/10 space-y-2">
                <button
                  onClick={handleGenerateSummary}
                  disabled={isGeneratingSummary || transcriptHistory.length === 0}
                  className="w-full py-2 px-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2"
                >
                  {isGeneratingSummary ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3" />
                      Generate AI Summary
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowNotesModal(true)}
                  className="w-full py-2 px-3 border border-white/20 rounded-lg text-xs text-gray-300 hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-3 h-3" />
                  View All Meeting Notes
                </button>
                <p className="text-[10px] text-gray-600 text-center">
                  {transcriptHistory.length > 0
                    ? `${transcriptHistory.length} transcript entries captured`
                    : 'Full summary available after meeting ends'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Q&A - Hidden */}
        {/* <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">Q&A</p>
                <p className="text-xs text-gray-500">Audience questions</p>
              </div>
            </div>
            <ToggleSwitch
              checked={qaActive}
              onChange={setQaActive}
            />
          </div>
          {qaActive && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="space-y-2">
                <div className="bg-black/20 rounded-lg p-3">
                  <p className="text-xs text-gray-400 text-center">No questions yet</p>
                  <p className="text-[10px] text-gray-500 text-center mt-1">
                    Questions from attendees will appear here
                  </p>
                </div>
                <button
                  onClick={onDisplayQuestion}
                  className="w-full py-2 px-3 bg-purple-500/20 text-purple-400 rounded-lg text-xs hover:bg-purple-500/30 transition-colors"
                >
                  Display Question on Screen
                </button>
              </div>
            </div>
          )}
        </div> */}

        {/* Breakout Rooms - admins only (or whitelisted email) */}
        {(user?.isAdmin || user?.email === 'krishan.kothari@gmail.com') && (
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">Breakout Rooms</p>
                <p className="text-xs text-gray-500">Split into groups</p>
              </div>
            </div>
            <ToggleSwitch
              checked={breakoutToggle || breakoutRoomsActive}
              onChange={(val) => {
                if (!val && breakoutRoomsActive) {
                  setShowBreakoutEndConfirm(true);
                  return;
                }
                setBreakoutToggle(val);
              }}
            />
          </div>
          {(breakoutToggle || breakoutRoomsActive) && (
            <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
              <div className="space-y-2">
                {breakoutRooms.map((room, idx) => (
                  <div
                    key={room.roomId || idx}
                    className="flex items-center justify-between p-2 bg-black/20 rounded-lg"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-6 h-6 rounded bg-indigo-500/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-indigo-300">{idx + 1}</span>
                      </div>
                      {editingRoomId === room.roomId ? (
                        <input
                          autoFocus
                          type="text"
                          value={editingRoomName}
                          maxLength={60}
                          onChange={(e) => setEditingRoomName(e.target.value)}
                          onBlur={commitEditingRoom}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitEditingRoom(); }
                            else if (e.key === 'Escape') { e.preventDefault(); cancelEditingRoom(); }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="bg-white/10 border border-indigo-400/60 focus:border-indigo-400 focus:outline-none text-sm text-white rounded px-1.5 py-0.5 min-w-0 flex-1"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => room.roomId && startEditingRoom(room)}
                          disabled={!room.roomId}
                          title={room.roomId ? 'Click to rename' : ''}
                          className="group flex items-center gap-1 min-w-0 text-left rounded px-1 -mx-1 py-0.5 hover:bg-white/5 transition-colors"
                        >
                          <span className="text-sm text-white truncate">{room.roomName}</span>
                          {room.roomId && (
                            <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          )}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{(room.participants || []).length} participants</span>
                      {breakoutRooms.length > 1 && !breakoutRoomsActive && (
                        <button
                          className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          onClick={() => room.roomId && removeRoom(room.roomId)}
                          title="Delete room"
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      )}
                      <button
                        className="p-1 hover:bg-white/10 rounded transition-colors"
                        onClick={() => {
                          if (!breakoutSession) createSession();
                          setShowBreakoutModal(true);
                        }}
                      >
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="w-full py-2 px-3 border border-dashed border-white/20 rounded-lg text-xs text-gray-400 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-2"
                onClick={() => {
                  if (addingRoomToolsRef.current) return;
                  addingRoomToolsRef.current = true;
                  setTimeout(() => { addingRoomToolsRef.current = false; }, 600);
                  const nextNum = breakoutRooms.length + 1;
                  // Ensure a breakout session exists before adding a room
                  if (!breakoutSession) {
                    createSession();
                    setTimeout(() => { addRoom(`Room ${nextNum}`); }, 300);
                  } else {
                    addRoom(`Room ${nextNum}`);
                  }
                }}
              >
                <Plus className="w-3 h-3" />
                Add Room
              </button>

              <button
                className="w-full py-2 px-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-medium transition-colors"
                onClick={() => {
                  if (!breakoutSession) createSession();
                  setShowBreakoutModal(true);
                }}
              >
                Manage Rooms
              </button>
            </div>
          )}

          {/* Confirmation dialog when trying to disable while rooms are active */}
          {showBreakoutEndConfirm && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg space-y-2">
              <p className="text-xs text-red-300">
                Breakout rooms are currently active. Turning this off will end all breakout sessions and return participants to the main room.
              </p>
              <div className="flex gap-2">
                <button
                  className="flex-1 py-1.5 px-3 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-colors"
                  onClick={() => {
                    closeRooms();
                    setBreakoutToggle(false);
                    setShowBreakoutEndConfirm(false);
                  }}
                >
                  End & Disable
                </button>
                <button
                  className="flex-1 py-1.5 px-3 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-medium transition-colors"
                  onClick={() => setShowBreakoutEndConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* AI Agents - Hidden */}
        {/* <div className="bg-white/5 rounded-xl p-4 relative overflow-hidden">
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded-full">
              Coming Soon
            </span>
          </div>
          <div className="flex items-center gap-3 opacity-50">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="font-medium text-white text-sm">AI Agents</p>
              <p className="text-xs text-gray-500">Virtual assistants</p>
            </div>
          </div>
        </div> */}

        {/* Private Chat - Hidden */}
        {/* <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <p className="font-medium text-white text-sm">Private Chat</p>
              <p className="text-xs text-gray-500">One-on-one messaging</p>
            </div>
          </div>
          <div className="relative">
            <select
              value={privateChatParticipant}
              onChange={(e) => setPrivateChatParticipant(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer"
            >
              <option value="" className="bg-[#001233]">Select participant...</option>
              {participants.map((p, i) => (
                <option key={i} value={p.id || p.name} className="bg-[#001233]">
                  {p.name || p.displayName || `Participant ${i + 1}`}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div> */}
      </div>

      {/* Meeting Notes Modal */}
      <MeetingNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        aiSummary={aiSummary}
        isLoading={isGeneratingSummary}
        transcriptHistory={transcriptHistory}
      />
    </div>
  );
}
